import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, RefreshCw, Eye, Download, X, Copy, ChevronLeft, 
  ChevronRight, Camera, Sparkles, CheckCircle, XCircle, Clock,
  User, Shirt, Image, FileImage, Timer, HardDrive, ExternalLink,
  Loader2, ZoomIn, Building2, Mail, ChevronDown, ChevronUp, 
  Calendar, ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { operationsAPI, adminAPI } from '../../api';
import { format, parseISO, subHours, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import { formatCredits } from '../../utils/credits';

// Quick date presets
const DATE_PRESETS = [
  { label: 'Last 1 hour', value: '1h', getDate: () => subHours(new Date(), 1) },
  { label: 'Last 24 hours', value: '24h', getDate: () => subHours(new Date(), 24) },
  { label: 'Last 2 days', value: '2d', getDate: () => subDays(new Date(), 2) },
  { label: 'Last 7 days', value: '7d', getDate: () => subDays(new Date(), 7) },
  { label: 'Last 30 days', value: '30d', getDate: () => subDays(new Date(), 30) },
  { label: 'Custom', value: 'custom', getDate: () => null }
];

// Sortable columns
const SORTABLE_COLUMNS = {
  created_at: { label: 'Created', key: 'created_at' },
  credits_debited: { label: 'Credits', key: 'credits_debited' },
  duration_ms: { label: 'Duration', key: 'duration_ms' }
};

// Sortable Table Header Component
const SortableHeader = ({ column, label, sortConfig, onSort }) => {
  const isActive = sortConfig.key === column;
  return (
    <th 
      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          sortConfig.direction === 'asc' ? 
            <ArrowUp className="w-3 h-3 text-blue-600" /> : 
            <ArrowDown className="w-3 h-3 text-blue-600" />
        ) : (
          <ArrowUpDown className="w-3 h-3 text-slate-400" />
        )}
      </div>
    </th>
  );
};

const AdminOperations = () => {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, hasNext: false, hasPrev: false });
  const [filters, setFilters] = useState({
    partner_id: '',
    operation_id: '',
    api_type: '',
    status: '',
    sku_id: '',
    start_date: '',
    end_date: '',
    date_preset: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [showFilters, setShowFilters] = useState(true);
  const [detailsModal, setDetailsModal] = useState({ open: false, operation: null, artifacts: [], loading: false, partner: null, tickets: [] });
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  
  // Partner search state
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerResults, setPartnerResults] = useState([]);
  const [searchingPartners, setSearchingPartners] = useState(false);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const partnerSearchRef = useRef(null);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const ARTIFACT_CONFIG = {
    INPUT_HUMAN: { icon: User, label: 'Human Image', color: 'blue' },
    INPUT_GARMENT: { icon: Shirt, label: 'Garment Image', color: 'purple' },
    OUTPUT_TRYON: { icon: Camera, label: 'Try-On Result', color: 'emerald' },
    INPUT_FOR_MOMENTS: { icon: Camera, label: 'Input Image', color: 'blue' },
    OUTPUT_MOMENTS: { icon: Sparkles, label: 'Moments Result', color: 'purple' },
    THUMBNAIL: { icon: Image, label: 'Thumbnail', color: 'slate' }
  };

  // Search partners
  const searchPartners = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setPartnerResults([]);
      return;
    }
    try {
      setSearchingPartners(true);
      const response = await adminAPI.getPartners({ search: query, page_size: 10 });
      setPartnerResults(response.data.partners || []);
    } catch (error) {
      console.error('Failed to search partners:', error);
    } finally {
      setSearchingPartners(false);
    }
  }, []);

  // Debounced partner search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (partnerSearch) {
        searchPartners(partnerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [partnerSearch, searchPartners]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (partnerSearchRef.current && !partnerSearchRef.current.contains(e.target)) {
        setShowPartnerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchOperations = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: 20,
        sort_by: sortConfig.key,
        sort_order: sortConfig.direction,
        ...filters 
      };
      
      // Handle date preset
      if (filters.date_preset && filters.date_preset !== 'custom') {
        const preset = DATE_PRESETS.find(p => p.value === filters.date_preset);
        if (preset) {
          params.start_date = preset.getDate().toISOString().split('T')[0];
          params.end_date = new Date().toISOString().split('T')[0];
        }
      }
      
      // Remove empty filters
      Object.keys(params).forEach(key => { 
        if (!params[key] || params[key] === '') delete params[key]; 
      });
      
      const response = await operationsAPI.adminList(params);
      let ops = response.data.operations || [];
      
      // Client-side sorting (if backend doesn't support)
      if (sortConfig.key) {
        ops = [...ops].sort((a, b) => {
          let aVal = a[sortConfig.key];
          let bVal = b[sortConfig.key];
          
          if (sortConfig.key === 'created_at') {
            aVal = new Date(aVal || 0).getTime();
            bVal = new Date(bVal || 0).getTime();
          } else {
            aVal = Number(aVal) || 0;
            bVal = Number(bVal) || 0;
          }
          
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        });
      }
      
      setOperations(ops);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        hasNext: response.data.has_next,
        hasPrev: response.data.has_prev
      });
    } catch (error) {
      toast.error('Failed to load operations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig]);

  useEffect(() => {
    fetchOperations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    fetchOperations(1);
  };

  const clearFilters = () => {
    setFilters({
      partner_id: '',
      operation_id: '',
      api_type: '',
      status: '',
      sku_id: '',
      start_date: '',
      end_date: '',
      date_preset: ''
    });
    setSelectedPartner(null);
    setPartnerSearch('');
  };

  const handleDatePreset = (preset) => {
    if (preset === 'custom') {
      setFilters(prev => ({ ...prev, date_preset: 'custom' }));
    } else {
      const presetConfig = DATE_PRESETS.find(p => p.value === preset);
      if (presetConfig) {
        setFilters(prev => ({ 
          ...prev, 
          date_preset: preset,
          start_date: '',
          end_date: ''
        }));
      }
    }
  };

  const handleSort = (columnKey) => {
    setSortConfig(prev => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const selectPartner = (partner) => {
    setSelectedPartner(partner);
    setFilters(prev => ({ ...prev, partner_id: partner.partner_id }));
    setPartnerSearch(partner.business_name || partner.email);
    setShowPartnerDropdown(false);
  };

  const clearPartnerFilter = () => {
    setSelectedPartner(null);
    setFilters(prev => ({ ...prev, partner_id: '' }));
    setPartnerSearch('');
  };

  const openDetailsModal = async (operation) => {
    setDetailsModal({ open: true, operation, artifacts: [], loading: true, partner: null, tickets: [] });
    
    try {
      const response = await operationsAPI.adminGetDetail(operation.operation_id);
      setDetailsModal(prev => ({
        ...prev,
        operation: response.data.operation,
        artifacts: response.data.artifacts || [],
        partner: response.data.partner,
        tickets: response.data.tickets || [],
        loading: false
      }));
    } catch (error) {
      console.error('Failed to fetch operation details:', error);
      setDetailsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-operations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Operations Ledger</h1>
          <p className="text-sm text-slate-500">View and filter all operations across partners</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg flex items-center gap-1.5 text-xs sm:text-sm ${showFilters ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'} hover:bg-slate-200`}
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filters</span>
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={() => fetchOperations(pagination.page)}
            className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-1.5 text-xs sm:text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-white rounded-xl border border-slate-200 p-5 overflow-hidden"
          >
            {/* Date Presets */}
            <div className="mb-4">
              <label className="text-xs text-slate-500 block mb-2">Quick Date Range</label>
              <div className="flex flex-wrap gap-2">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => handleDatePreset(preset.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filters.date_preset === preset.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Date Range (shown when custom is selected) */}
            {filters.date_preset === 'custom' && (
              <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">End Date</label>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {/* Partner Search with Autocomplete */}
              <div className="col-span-2" ref={partnerSearchRef}>
                <label className="text-xs text-slate-500 block mb-1">Partner</label>
                <div className="relative">
                  <div className="flex items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={partnerSearch}
                        onChange={(e) => {
                          setPartnerSearch(e.target.value);
                          setShowPartnerDropdown(true);
                          if (!e.target.value) {
                            setFilters(prev => ({ ...prev, partner_id: '' }));
                            setSelectedPartner(null);
                          }
                        }}
                        onFocus={() => setShowPartnerDropdown(true)}
                        placeholder="Search by email or business name..."
                        className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm"
                      />
                      {selectedPartner && (
                        <button 
                          onClick={clearPartnerFilter}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Dropdown */}
                  {showPartnerDropdown && (partnerResults.length > 0 || searchingPartners) && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchingPartners ? (
                        <div className="p-3 text-center text-slate-500">
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        </div>
                      ) : (
                        partnerResults.map((partner) => (
                          <button
                            key={partner.partner_id}
                            onClick={() => selectPartner(partner)}
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          >
                            <div className="font-medium text-sm text-slate-800">
                              {partner.business_name || 'No Business Name'}
                            </div>
                            <div className="text-xs text-slate-500">{partner.email}</div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {selectedPartner && (
                  <div className="mt-1 text-xs text-blue-600">
                    Filtering: {selectedPartner.business_name || selectedPartner.email}
                  </div>
                )}
              </div>

              {/* Operation ID */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">Operation ID</label>
                <input
                  type="text"
                  value={filters.operation_id}
                  onChange={(e) => setFilters({ ...filters, operation_id: e.target.value })}
                  placeholder="Operation ID..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>

              {/* API Type */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">API Type</label>
                <select
                  value={filters.api_type}
                  onChange={(e) => setFilters({ ...filters, api_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                >
                  <option value="">All Types</option>
                  <option value="TRYON">TryOn</option>
                  <option value="MOMENTS">Moments</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                >
                  <option value="">All Statuses</option>
                  <option value="SUCCESS">Success</option>
                  <option value="FAILED">Failed</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>

              {/* SKU ID */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">SKU ID</label>
                <input
                  type="text"
                  value={filters.sku_id}
                  onChange={(e) => setFilters({ ...filters, sku_id: e.target.value })}
                  placeholder="SKU..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <div className="text-sm text-slate-500">
                {Object.values(filters).filter(v => v).length > 0 && (
                  <span>{Object.values(filters).filter(v => v).length} filter(s) active</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm"
                >
                  Clear All
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex flex-wrap items-center gap-3 sm:gap-6">
            <p className="text-xs sm:text-sm text-slate-600">
              Total: <span className="font-semibold text-slate-800">{pagination.total.toLocaleString()}</span>
            </p>
            {selectedPartner && (
              <p className="text-xs sm:text-sm text-blue-600 truncate max-w-[150px] sm:max-w-none">
                Partner: <span className="font-semibold">{selectedPartner.business_name || selectedPartner.email}</span>
              </p>
            )}
          </div>
          <div className="text-xs text-slate-500">
            Sort: <span className="font-medium">{SORTABLE_COLUMNS[sortConfig.key]?.label || sortConfig.key}</span>
          </div>
        </div>
      </div>

      {/* Operations Table/Cards */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : operations.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-lg font-medium">No operations found</p>
            <p className="text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Preview</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Operation ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Partner</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Status</th>
                    <SortableHeader column="credits_debited" label="Credits" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader column="duration_ms" label="Duration" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader column="created_at" label="Created" sortConfig={sortConfig} onSort={handleSort} />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {operations.map((op) => (
                    <tr key={op.operation_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        {op.thumbnail_url || op.output_url ? (
                          <img 
                            src={op.thumbnail_url || op.output_url} 
                            alt="" 
                            className="w-12 h-12 object-cover rounded-lg cursor-pointer hover:opacity-80"
                            onClick={() => { setViewerUrl(op.output_url || op.thumbnail_url); setViewerOpen(true); }}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Image className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {op.api_type === 'TRYON' ? (
                            <Camera className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Sparkles className="w-4 h-4 text-purple-600" />
                          )}
                          <span className="text-sm font-medium">{op.api_type}</span>
                          {op.is_cached && (
                            <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded">Cached</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <code className="text-xs text-slate-600 font-mono">{op.operation_id.slice(0, 12)}...</code>
                          <button onClick={() => copyToClipboard(op.operation_id, 'ID')} className="p-1 hover:bg-slate-100 rounded">
                            <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <div className="font-medium text-slate-800">{op.partner_name || '-'}</div>
                          <div className="text-xs text-slate-500">{op.partner_email || op.partner_id?.slice(0, 12)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          op.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700' :
                          op.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                          op.status === 'PENDING' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {op.status === 'SUCCESS' ? <CheckCircle className="w-3 h-3" /> :
                           op.status === 'FAILED' ? <XCircle className="w-3 h-3" /> :
                           <Clock className="w-3 h-3" />}
                          {op.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium font-mono">{formatCredits(op.credits_debited)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 font-mono">{op.duration_ms ? `${op.duration_ms}ms` : '-'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {op.created_at ? format(parseISO(op.created_at), 'MMM d, HH:mm:ss') : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openDetailsModal(op)}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile/Tablet Card View */}
            <div className="lg:hidden divide-y divide-slate-100">
              {operations.map((op) => (
                <div key={op.operation_id} className="p-4 hover:bg-slate-50">
                  <div className="flex gap-3">
                    {/* Preview Image */}
                    <div className="flex-shrink-0">
                      {op.thumbnail_url || op.output_url ? (
                        <img 
                          src={op.thumbnail_url || op.output_url} 
                          alt="" 
                          className="w-16 h-16 object-cover rounded-lg cursor-pointer"
                          onClick={() => { setViewerUrl(op.output_url || op.thumbnail_url); setViewerOpen(true); }}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Image className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          {op.api_type === 'TRYON' ? (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">TryOn</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">Moments</span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            op.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700' :
                            op.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {op.status}
                          </span>
                        </div>
                        <button
                          onClick={() => openDetailsModal(op)}
                          className="p-1.5 hover:bg-slate-200 rounded-lg"
                        >
                          <Eye className="w-4 h-4 text-slate-600" />
                        </button>
                      </div>
                      
                      <p className="text-xs text-slate-500 truncate mb-1">
                        {op.partner_name || op.partner_email || 'Unknown Partner'}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
                        <span className="font-mono">{formatCredits(op.credits_debited)} cr</span>
                        {op.duration_ms && <span>{op.duration_ms}ms</span>}
                        <span>{op.created_at ? format(parseISO(op.created_at), 'MMM d, HH:mm') : '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Page {pagination.page} of {Math.ceil(pagination.total / 20)} ({pagination.total.toLocaleString()} total)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => fetchOperations(pagination.page - 1)}
              disabled={!pagination.hasPrev}
              className="px-3 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <button
              onClick={() => fetchOperations(pagination.page + 1)}
              disabled={!pagination.hasNext}
              className="px-3 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <AnimatePresence>
        {detailsModal.open && detailsModal.operation && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-lg font-bold">Operation Details (Admin View)</h2>
                  <p className="text-sm text-slate-500 font-mono">{detailsModal.operation.operation_id}</p>
                </div>
                <button onClick={() => setDetailsModal({ open: false, operation: null, artifacts: [], partner: null, tickets: [] })} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {detailsModal.loading ? (
                <div className="p-12 flex justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  {/* Partner Info */}
                  {detailsModal.partner && (
                    <div className="bg-blue-50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Partner Information
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-blue-600">Business</p>
                          <p className="font-medium text-slate-800">{detailsModal.partner.business_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-blue-600">Email</p>
                          <p className="font-medium text-slate-800">{detailsModal.partner.email}</p>
                        </div>
                        <div>
                          <p className="text-blue-600">Plan</p>
                          <p className="font-medium text-slate-800 capitalize">{detailsModal.partner.subscription_plan || 'starter'}</p>
                        </div>
                        <div>
                          <p className="text-blue-600">Partner ID</p>
                          <p className="font-medium text-slate-800 font-mono text-xs">{detailsModal.partner.partner_id?.slice(0, 12)}...</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Operation Info Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">API Type</p>
                      <p className="font-semibold flex items-center gap-2">
                        {detailsModal.operation.api_type === 'TRYON' ? <Camera className="w-4 h-4 text-blue-600" /> : <Sparkles className="w-4 h-4 text-purple-600" />}
                        {detailsModal.operation.api_type}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Status</p>
                      <p className={`font-semibold ${detailsModal.operation.status === 'SUCCESS' ? 'text-emerald-600' : detailsModal.operation.status === 'FAILED' ? 'text-red-600' : 'text-amber-600'}`}>
                        {detailsModal.operation.status}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Credits Debited</p>
                      <p className="font-semibold">{formatCredits(detailsModal.operation.credits_debited)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Duration</p>
                      <p className="font-semibold">{detailsModal.operation.duration_ms ? `${detailsModal.operation.duration_ms}ms` : '-'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Created</p>
                      <p className="font-semibold text-sm">{detailsModal.operation.created_at ? format(parseISO(detailsModal.operation.created_at), 'MMM d, yyyy HH:mm:ss') : '-'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Cached</p>
                      <p className="font-semibold">{detailsModal.operation.is_cached ? 'Yes (Cache Hit)' : 'No'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Source</p>
                      <p className="font-semibold">{detailsModal.operation.source || '-'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">SKU ID</p>
                      <p className="font-semibold font-mono text-xs">{detailsModal.operation.sku_id || '-'}</p>
                    </div>
                  </div>

                  {/* Artifacts */}
                  {detailsModal.artifacts.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3">Artifacts ({detailsModal.artifacts.length})</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {detailsModal.artifacts.map((artifact, idx) => {
                          const config = ARTIFACT_CONFIG[artifact.artifact_type] || { icon: FileImage, label: artifact.artifact_type, color: 'slate' };
                          const Icon = config.icon;
                          return (
                            <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                              {artifact.url && (
                                <img 
                                  src={artifact.url} 
                                  alt={config.label}
                                  className="w-full h-40 object-cover cursor-pointer hover:opacity-90"
                                  onClick={() => { setViewerUrl(artifact.url); setViewerOpen(true); }}
                                />
                              )}
                              <div className="p-3 bg-slate-50">
                                <div className="flex items-center gap-2 mb-1">
                                  <Icon className={`w-4 h-4 text-${config.color}-600`} />
                                  <span className="text-sm font-medium">{config.label}</span>
                                </div>
                                <div className="text-xs text-slate-500">
                                  {formatFileSize(artifact.file_size)}
                                  {artifact.dimensions && ` • ${artifact.dimensions}`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Error Details */}
                  {detailsModal.operation.error_message && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-red-800 mb-2">Error Details</h3>
                      <p className="text-sm text-red-700">{detailsModal.operation.error_message}</p>
                      {detailsModal.operation.error_code && (
                        <p className="text-xs text-red-600 mt-1 font-mono">Code: {detailsModal.operation.error_code}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Image Viewer Modal */}
      <AnimatePresence>
        {viewerOpen && viewerUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center"
            onClick={() => setViewerOpen(false)}
          >
            <button 
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
              onClick={() => setViewerOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={viewerUrl} 
              alt="Full size" 
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminOperations;
